/** Server compile on the project wiki page: credit estimate, start button, and progress of the latest compile */

import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { checkScope, PERMISSIONS, type WikiCompileJob, type WikiCompilePageResult } from '@agentwiki/shared'
import { useAuth } from '../../hooks/use-auth'
import { useCompileEstimate, useLatestCompileJob, useStartCompile } from '../../hooks/use-project-content'
import { projectPath, projectReviewPath } from '../shell/nav-config'
import { useT } from '../../i18n'
import { cn } from '../../lib/utils'
import { badgeClass, buttonPrimaryClass, errorTextClass, hintClass } from '../ui/styles'

const PAGE_STATUS_KEY = {
  applied: 'compile.page.applied',
  pending: 'compile.page.pending',
  failed: 'compile.page.failed',
} as const satisfies Record<WikiCompilePageResult['status'], string>

function JobStatus({ job, project }: { job: WikiCompileJob; project: string }) {
  const t = useT()
  const open = job.status === 'queued' || job.status === 'running'
  const status = job.status === 'running'
    ? (job.plannedPages ? t('compile.status.running', { done: job.pages.length, planned: job.plannedPages }) : t('compile.status.planning'))
    : t(`compile.status.${job.status}`)

  return (
    <div className="space-y-2 border-t border-border pt-3">
      <p className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium text-fg">{t('compile.lastJob')}</span>
        <span
          role="status"
          className={cn(badgeClass, job.status === 'failed' && 'border-danger/40 text-danger', job.status === 'succeeded' && 'text-fg')}
        >
          {status}
        </span>
        {!open && <span className={hintClass}>{t('compile.charged', { charged: job.creditsCharged, reserved: job.creditsReserved })}</span>}
      </p>
      {job.error && <p className="text-sm text-danger">{job.error}</p>}
      {job.pages.length > 0 && (
        <ul className="divide-y divide-border rounded-lg border border-border text-sm">
          {job.pages.map((page) => (
            <li key={page.slug} className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2">
              <span className="min-w-0">
                {page.status === 'applied' ? (
                  <Link to={projectPath(project, `wiki/${page.slug}`)} className="font-medium text-fg hover:underline">{page.title}</Link>
                ) : (
                  <span className="font-medium text-fg">{page.title}</span>
                )}
                <span className="ml-2 text-xs text-fg-subtle">{page.topic}</span>
                {page.error && <span className="block text-xs text-danger">{page.error}</span>}
              </span>
              <span className={cn('shrink-0 text-xs', page.status === 'failed' ? 'text-danger' : 'text-fg-subtle')}>
                {t(PAGE_STATUS_KEY[page.status])}
              </span>
            </li>
          ))}
        </ul>
      )}
      {job.pages.some((p) => p.status === 'pending') && (
        <Link to={projectReviewPath(project)} className="inline-block text-sm text-accent hover:underline">{t('compile.review')}</Link>
      )}
    </div>
  )
}

export function ServerCompilePanel({ project }: { project: string }) {
  const t = useT()
  const { role } = useAuth()
  const estimate = useCompileEstimate(project)
  const data = estimate.data
  // A compile started from the CLI, MCP, or another tab shows up here and is followed while it runs
  const latest = useLatestCompileJob(project, data?.activeJobId ?? null)
  const start = useStartCompile(project)

  const canStart = role ? checkScope(PERMISSIONS[role] ?? [], 'doc:update') : false
  const job = latest.data ?? null
  const running = job?.status === 'queued' || job?.status === 'running' || Boolean(data?.activeJobId)
  const short = data ? data.balance < data.credits : false
  const blocked = !data || !data.provider || data.sourceCount === 0 || short || running

  const handleStart = async () => {
    if (!data || !window.confirm(t('compile.confirm', { credits: data.credits }))) return
    try {
      await start.mutateAsync()
      toast.success(t('compile.started'))
    } catch (err) {
      toast.error(t('compile.startFailed', { message: err instanceof Error ? err.message : String(err) }))
    }
  }

  return (
    <section aria-labelledby="server-compile-title" className="space-y-3 rounded-xl border border-border bg-surface-raised p-4 md:p-5">
      <div>
        <h2 id="server-compile-title" className="font-display text-lg font-semibold text-fg">{t('compile.title')}</h2>
        <p className="max-w-2xl text-sm text-fg-muted">{t('compile.hint')}</p>
      </div>

      {estimate.isLoading ? (
        <p className={hintClass}>{t('compile.loading')}</p>
      ) : estimate.error || !data ? (
        <p role="alert" className={errorTextClass}>{t('compile.loadError')}</p>
      ) : (
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="space-y-1 text-sm">
            <p className="font-display text-2xl font-semibold tabular-nums text-fg">{t('compile.cost', { credits: data.credits })}</p>
            <p className="text-fg-muted">
              {t(data.sourceCount === 1 ? 'compile.sources.one' : 'compile.sources', { count: data.sourceCount, chars: data.sourceChars.toLocaleString() })}
              {' · '}
              {t('compile.balance', { balance: data.balance })}
            </p>
            {data.provider && <p className={hintClass}>{t('compile.provider', { provider: `${data.provider.providerId} / ${data.provider.model}` })}</p>}
            {data.truncated && <p className={hintClass}>{t('compile.truncated')}</p>}
            {!data.provider && <p className="text-danger">{t('compile.noProvider')}</p>}
            {data.sourceCount === 0 && <p className="text-danger">{t('compile.noSources')}</p>}
            {short && (
              <p className="text-danger">
                {t('compile.insufficient', { credits: data.credits })}{' '}
                <Link to="/billing" className="text-accent hover:underline">{t('compile.buyCredits')}</Link>
              </p>
            )}
          </div>
          {canStart ? (
            <button
              type="button"
              onClick={() => void handleStart()}
              disabled={blocked || start.isPending}
              className={cn(buttonPrimaryClass, 'shrink-0')}
            >
              {start.isPending ? t('compile.starting') : t('compile.start', { credits: data.credits })}
            </button>
          ) : (
            <p className={hintClass}>{t('compile.needsEditor')}</p>
          )}
        </div>
      )}

      {job && <JobStatus job={job} project={project} />}
    </section>
  )
}
